import React from "react";
import { Button } from "react-bootstrap";
import { format } from "date-fns";
import { get } from "../../api/usersModule/httpClient";
import Swal from "sweetalert2";
import "./ModalOpenNotification.css";

export const ModalOpenNotification = ({ closeModal, selectedNotification }) => {
  const linkRegex = /(https?:\/\/\S+)/i;
  const match = selectedNotification?.message.match(linkRegex);
  const link = match ? match[0] : null;

  // Función para detectar y parsear notificaciones de dispatch orders
  const parseDispatchOrderNotification = (message) => {
    // Patrón flexible para detectar notificaciones de dispatch orders
    // Acepta proveedor con corchetes [Proveedor] o sin corchetes (p.ej., "BIOPLAST DE ANTIOQUIA S.A.S.")
    const dispatchOrderPattern = /Le informamos que se ha actualizado la orden de despacho con identificador\s+([A-Z0-9-]+)\s+por parte de\s+(?:\[([^\]]+)\]|(.+?))\.\s+Se han actualizado los siguientes productos:\s+(.+)/i;

    const match = message.match(dispatchOrderPattern);
    if (!match) return null;

    const [, orderId, providerBracket, providerPlain, productsText] = match;
    const providerName = (providerBracket || providerPlain || '').trim();

    // Parsear productos: permite descripción opcional y maneja fin de texto o siguiente SKU
    const productPattern = /SKU\s+([A-Z0-9]+):\s*([\d.,]+)\s+unidades(?:,\s+descripción:\s+([^,]+?))?(?=,\s+SKU|$)/gi;
    const products = [];
    let productMatch;

    while ((productMatch = productPattern.exec(productsText)) !== null) {
      const sku = productMatch[1];
      const quantity = (productMatch[2] || '').replace(/,/g, '');
      const description = (productMatch[3] || '').trim();
      products.push({ sku, quantity, description });
    }

    return { orderId, providerName, products };
  };

  // Verificar si es una notificación de dispatch order
  const dispatchOrderData = selectedNotification ? parseDispatchOrderNotification(selectedNotification.message) : null;

  const cleanMessage = (message) => {
    const regex = /https?:\/\/\S+/gi;
    return message.replace(regex, "");
  };

  const formatDateString = (dateString) => {
    const date = new Date(dateString);
    return format(date, "d/M/yyyy h:mm:ss a");
  };

  const formattedMessage = selectedNotification
    ? cleanMessage(selectedNotification.message).replace(
        /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z)/g,
        (_, match) => formatDateString(match)
      )
    : "";

  const generateURL = async (fileName, bucketName) => {
    try {
      const url = `/skus?generateURL=true&fileName=${encodeURIComponent(
        fileName
      )}&bucketName=${encodeURIComponent(bucketName)}`;

      const response = await get(url);

      if (response.answer) {
        window.open(response.url, "_blank");
      } else {
        Swal.fire({
          title: "Error",
          text: "Error al generar el enlace de descarga.",
          icon: "error",
          confirmButtonColor: "#146284",
        });
      }
    } catch (error) {
      console.error("Error generando el enlace:", error);
      Swal.fire({
        title: "Error",
        text: "Error al generar el enlace de descarga.",
        icon: "error",
        confirmButtonColor: "#146284",
      });
    }
  };

  const messageParts = formattedMessage.split("\n");

  return (
    <div className="modal-container">
      <div className="modal-header d-flex justify-content-between">
        <div className="col">
          <h5 className="modal-title">{selectedNotification?.title}</h5>
          <div className="subtitle">
            <span>
              <strong>Recibida:</strong> &nbsp; {selectedNotification?.date}
            </span>
            <span>
              <strong>Tipo:</strong> &nbsp;
              <span
                className={`text-${
                  selectedNotification?.type === "error"
                    ? "danger"
                    : selectedNotification?.type
                }`}
              >
                {selectedNotification?.type === "success"
                  ? "Éxito"
                  : selectedNotification?.type === "info"
                  ? "Información"
                  : selectedNotification?.type === "warning"
                  ? "Importante"
                  : selectedNotification?.type === "error"
                  ? "Cuidado"
                  : null}
              </span>
            </span>
          </div>
        </div>
      </div>
      <div className="modal-body">
        {/* Mostrar notificación de dispatch order de forma organizada */}
        {dispatchOrderData ? (
          <div className="dispatch-order-notification">
            <div className="dispatch-order-header">
              <h6 className="dispatch-order-title">
                📦 Actualización de Orden de Despacho
              </h6>
              <div className="dispatch-order-info">
                <p><strong>Orden ID:</strong> <span className="order-id">{dispatchOrderData.orderId}</span></p>
                <p><strong>Proveedor:</strong> <span className="provider-name">{dispatchOrderData.providerName}</span></p>
              </div>
            </div>
            
            <div className="dispatch-products-section">
              <h6 className="products-title">Unidades a Enviar:</h6>
              <div className="products-list">
                {dispatchOrderData.products.map((product, index) => (
                  <div key={index} className="product-container">
                    {product.description && (
                      <h6 className="product-title">{product.description}</h6>
                    )}
                    <div className="product-card">
                      <div className="product-sku">
                        <span className="sku-label">SKU:</span>
                        <span className="sku-code">{product.sku}</span>
                      </div>
                      <div className="product-quantity">
                        <span className="quantity-value">{parseInt(product.quantity).toLocaleString()}</span>
                        <span className="quantity-unit">unidades</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          // Mostrar mensaje normal si no es una notificación de dispatch order
          messageParts.map((part, index) => (
            <p key={index}>{part}</p>
          ))
        )}
        {/* Agrega la lista de SKUs debajo del mensaje si existe */}
        {selectedNotification?.skus_below_threshold && (
          <div className="skus-list-container">
            <h6>
              El porcentaje de inventario para los siguientes SKU ha cambiado y
              está por debajo del porcentaje permitido:
            </h6>
            <ul className="skus-list">
              {JSON.parse(selectedNotification.skus_below_threshold).map(
                (sku, index) => {
                  // Función para obtener el color basado en el porcentaje de inventario
                  const getColorClass = (percent) => {
                    if (percent <= 25) return "sku-percent-black";
                    if (percent > 25 && percent <= 50) return "sku-percent-red";
                    if (percent > 50 && percent <= 75)
                      return "sku-percent-yellow";
                    if (percent > 75 && percent <= 100)
                      return "sku-percent-green";
                    return "sku-percent-blue"; // Para porcentajes mayores a 100%
                  };

                  return (
                    <li key={index}>
                      <span className="sku-code">{sku.code_sap}:</span>
                      <span
                        className={`sku-percent ${getColorClass(
                          sku.percent_inventory
                        )}`}
                      >
                        {sku.percent_inventory.toFixed(2)}%
                      </span>
                      -{" "}
                      <span className="sku-description">
                        {sku.sku_description}
                      </span>
                    </li>
                  );
                }
              )}
            </ul>
          </div>
        )}
      </div>
      <div className="modal-footer">
        {link && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              var urlParts = link.split("/");
              var bucketName = urlParts[2];
              var fileNameWithExtension = urlParts[urlParts.length - 1];
              var bucketNameWithoutS3 = bucketName.split(".")[0];
              generateURL(fileNameWithExtension, bucketNameWithoutS3);
            }}
          >
            <i className="fas fa-cloud-download-alt"></i> Descargar
          </button>
        )}
        <Button variant="secondary" onClick={closeModal}>
          Cerrar
        </Button>
      </div>
    </div>
  );
};